/**
 * Base Specs Hook
 * Manages Base Spec CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthToken } from './use-auth';

// Types
export interface BaseSpec {
  id: string;
  projectId: string;
  title: string;
  version: string;
  vision: string | null;
  problemStatement: string | null;
  scope: Record<string, unknown> | null;
  assumptions: Record<string, unknown> | null;
  constraints: Record<string, unknown> | null;
  successCriteria: Record<string, unknown> | null;
  userStories: Record<string, unknown> | null;
  status: 'DRAFT' | 'IN_REVIEW' | 'APPROVED' | 'ARCHIVED';
  readyForContract: boolean;
  traceabilityMatrix: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  contracts?: Array<{ id: string; title: string; version: string; status: string }>;
}

// List base specs
export function useBaseSpecs(projectId: string) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['base-specs', projectId],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/base-specs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch base specs');
      const data = await response.json();
      return data.data as BaseSpec[];
    },
    enabled: !!projectId && !!token,
  });
}

// Get single base spec
export function useBaseSpec(projectId: string, specId: string) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['base-spec', projectId, specId],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/base-specs/${specId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch base spec');
      const data = await response.json();
      return data.data as BaseSpec;
    },
    enabled: !!projectId && !!specId && !!token,
  });
}

// Create base spec
export function useCreateBaseSpec(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Partial<BaseSpec>) => {
      const response = await fetch(`/api/projects/${projectId}/base-specs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create base spec');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['base-specs', projectId] });
    },
  });
}

// Update base spec
export function useUpdateBaseSpec(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ specId, data }: { specId: string; data: Partial<BaseSpec> }) => {
      const response = await fetch(`/api/projects/${projectId}/base-specs/${specId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update base spec');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['base-specs', projectId] });
      queryClient.invalidateQueries({ queryKey: ['base-spec', projectId, variables.specId] });
    },
  });
}

// Update base spec status
export function useUpdateBaseSpecStatus(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ specId, status, readyForContract }: { specId: string; status: BaseSpec['status']; readyForContract?: boolean }) => {
      const response = await fetch(`/api/projects/${projectId}/base-specs/${specId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status, readyForContract }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update base spec status');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['base-specs', projectId] });
      queryClient.invalidateQueries({ queryKey: ['base-spec', projectId, variables.specId] });
    },
  });
}

// Delete base spec
export function useDeleteBaseSpec(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (specId: string) => {
      const response = await fetch(`/api/projects/${projectId}/base-specs/${specId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete base spec');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['base-specs', projectId] });
    },
  });
}
